import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';

import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import Container from '@v2/shared/components/Container';
import { useUI } from '@v2/shared/lib/ui';
import { useCart } from '@v2/shared/lib/cart';
import { cn } from '@v2/shared/lib/utils';
import {
  normalizeRestrictions,
  PRODUCT_TYPE_MAGAZINE,
  SALE_CONTAINER,
  SaleProduct,
  scrollToSaleTarget,
  shouldHideProduct,
  useSale,
} from '@v2/shared/lib/megaSale';
import { trackAddToCart } from '@v2/shared/lib/analytics';

import CategoryNav from './CategoryNav';
import CategorySection from './CategorySection';
import GiftingPromo from './GiftingPromo';
import SaleErrorBoundary from './SaleErrorBoundary';
import SaleSkeleton from './SaleSkeleton';
import SaleBanner, { SaleBannerProps } from './SaleBanner';
import SaleCountdown from './SaleCountdown';
import SaleEndedDialog from './SaleEndedDialog';

export type MegaSalePageProps = {
  showCoverPrice?: boolean;
  /** The sale-wide `ht` flag: open each tile on its longest term rather than its default. */
  highestTerm?: boolean;
  /** Products this visitor must not be shown. Never cached — see the template. */
  restrictedProductIds?: (number | string)[];
  /** Hot-zone products, or `['*']` for "every flagged product". */
  hotzoneProductIds?: (number | string)[];
  siteName?: string;
  /**
   * Whether the sale has not started yet, rather than having ended — the two states the
   * ended dialog words differently. Which of them the page is in is the layout's
   * `saleOver`; this only says which sentence to print.
   */
  notYetActive?: boolean;
  /** When the sale ended, already formatted by the server. */
  expiryDate?: string | null;
  /** Seconds until the sale ends, when the section asks for a countdown. */
  expirySeconds?: number | null;
  banner?: SaleBannerProps['banner'];
  /**
   * Whether to interrupt the grid with "Did you know you can gift magazines to your
   * friends?" after the first section.
   *
   * The one thing on this page that is not the same on every brand. Off by default and
   * on only where a brand turns it on, which today is DiscountMags alone: it is the only
   * brand with a gifting landing page of its own, and the only one that has ever run this
   * promo. Gifting itself is not brand-gated — every storefront's cart takes
   * `send_as_gift` — so this is a marketing choice rather than a capability, and the day
   * another brand wants it, it is one word in that brand's own `Pages/MegaSale`.
   */
  showGiftingPromo?: boolean;
};

/**
 * The sale.
 *
 * The chrome, the sale data and the cart drawer are the layout's — see
 * `Layouts/MegaSale` — because the header needs the same sale this page draws. What is
 * here is the grid, and the restrictions, which are the visitor's and so never leave
 * this component's props.
 */
export default function MegaSale({
  showCoverPrice = true,
  highestTerm = false,
  restrictedProductIds = [],
  hotzoneProductIds = [],
  notYetActive = false,
  expiryDate = null,
  expirySeconds = null,
  banner = null,
  showGiftingPromo = false,
}: MegaSalePageProps) {
  const { status, data, error, retry, selection, select, giftOffer, cartDrawer, publishRestrictions } =
    useSale();
  const { addItem } = useCart();
  const { headerHeight } = useUI();

  const restrictions = useMemo(
    () => normalizeRestrictions(restrictedProductIds, hotzoneProductIds),
    [restrictedProductIds, hotzoneProductIds]
  );

  // The first section this visitor will actually see — the one the gifting block follows.
  const firstDrawnCategoryId = useMemo(
    () =>
      data?.categories.find((category) =>
        (data.productsByCategory[category.id] || []).some(
          (product) => !shouldHideProduct(product, restrictions)
        )
      )?.id ?? null,
    [data, restrictions]
  );

  // The note under the grid is about magazine covers and subscriptions, so a sale with no
  // magazine in it — the postcards sale — does not carry it.
  const sellsMagazines = useMemo(
    () =>
      (data?.categories ?? []).some((category) =>
        (data?.productsByCategory[category.id] || []).some(
          (product) => product.product_type === PRODUCT_TYPE_MAGAZINE
        )
      ),
    [data]
  );

  // The header's search box has to hide the same products this grid does, and the header
  // is the layout's. See SaleContextValue.restrictions for why it travels this way.
  useEffect(() => publishRestrictions(restrictions), [restrictions, publishRestrictions]);

  /*
   * The header's height, as a custom property.
   *
   * Both the sticky category headings and the sticky rail have to clear the fixed header,
   * and it has three heights across the breakpoints. Publishing the measured value once
   * means neither of them states a number that is right at one width and wrong at the
   * others.
   */
  useEffect(() => {
    if (headerHeight) {
      document.documentElement.style.setProperty('--sale-header-height', `${headerHeight}px`);
    }
  }, [headerHeight]);

  /*
   * The outstanding "open this product's detail" request, and nothing more.
   *
   * It lives here rather than in the tile because the tile does not survive: it is
   * unmounted as soon as it scrolls out of the render window. That is also why it has to
   * be cleared the moment the dialog closes — a request left standing is handed straight
   * back to the tile when it mounts again on the way up, and a dialog the customer closed
   * a scroll ago opens itself. One selection, one opening.
   *
   * It cannot be `selection.openDetail` read directly, and clearing that instead would be
   * worse than doing nothing: `selection` is the scroll effect's dependency, so writing to
   * it to record that the dialog is finished with would start a second journey to a place
   * the customer has already left.
   */
  const [detail, setDetail] = useState<{ categoryId: number; productId: number; request: number } | null>(
    null
  );

  /*
   * Going to a place in the sale: a deep link, a category from the navbar or the mobile
   * menu, or a product from the scoped search.
   *
   * All four are the same operation, which is why there is one effect. It runs on the
   * selection's *request number* rather than on the category and product, so choosing the
   * same suggestion twice scrolls twice — the values had not changed, so nothing here ran
   * a second time and the page stayed where the customer had left it.
   *
   * It deliberately does not depend on the header's height. That changes mid-journey on a
   * phone, where the search bar collapses as soon as the page moves, and re-running would
   * restart the scroll rather than correct it. `scrollToSaleTarget` re-measures on every
   * frame instead, which is also what makes it land correctly when a skeleton ahead of the
   * target becomes a taller product.
   *
   * The deep-linked tile is pinned as well, so the windowing cannot unmount the thing
   * being scrolled to — see `pinnedProductId` below.
   */
  useEffect(() => {
    const { categoryId, productId, openDetail, request } = selection;

    if (status !== 'loaded' || categoryId === null) {
      return;
    }

    return scrollToSaleTarget({
      elementId: productId ? `prod_${categoryId}_${productId}` : `sect_wrapper${categoryId}`,
      categoryId,
      behindHeading: productId !== null,
      /*
       * The detail view opens on arrival and not before, which is the order the legacy
       * page used and for a reason that still applies: the dialog holds a scroll lock
       * while it is open, so opening it first freezes the page where the customer already
       * was and the scroll never happens.
       */
      onSettled: () => {
        if (openDetail && productId !== null) {
          setDetail({ categoryId, productId, request });
        }
      },
    });
  }, [status, selection]);

  /**
   * Adds a product, then offers a second copy as a gift.
   *
   * The offer needs the *gift* sku rather than the one just bought — a gift is the New
   * type of the same term — and the tile is the only thing that knows it, so it comes
   * along with the add.
   *
   * The drawer now opens at every width. It used to open only on a phone, because the
   * gift offer was drawn over the tile on a desktop and the drawer's backdrop landed on
   * top of it — the upsell was visible and unclickable. The offer has moved into the
   * cart, so that conflict is gone and the rule that existed to avoid it goes with it.
   *
   * The line the add produced travels with the offer so the cart can draw it against the
   * right product rather than at the top. It is the last line of the returned cart: the
   * api appends, and the response is the whole cart after the write.
   */
  const onAdd = async (
    product: SaleProduct,
    skuCode: string,
    giftSkuCode: string | null,
    offerCode: string | null
  ) => {
    const cart = await addItem(skuCode, offerCode);
    const added = cart.items?.[cart.items.length - 1];

    /*
     * The GA4 add, from the line the server actually wrote rather than from the tile's
     * own idea of what it asked for — so the product and the price reported are the ones
     * in the cart. Every add on this page arrives here (a tile, the (i) dialog, the
     * Magzter Gold card), so this is the only place it needs saying; the gift copy is the
     * layout's and reports itself.
     */
    if (added) {
      trackAddToCart(added);
    }

    /*
     * Every add answers the outstanding offer, one way or the other.
     *
     * Raising a new one is only half of "the previous upsells should be dismissed and not
     * displayed again". Not everything on a sale can be gifted — a book has no Type
     * variant and so no gift sku — and until this dismissed as well, adding a book left
     * the offer standing over the magazine two adds ago, which is the same stale upsell
     * from a different direction.
     *
     * The offer is drawn under the line the add produced and nowhere else, so one that
     * could not be anchored to a line has nothing to be drawn against either.
     */
    if (giftSkuCode && added) {
      giftOffer.offer(product, giftSkuCode, offerCode, added.id);
    } else {
      giftOffer.dismiss();
    }

    cartDrawer.open();
  };

  /*
   * A finished sale is the artwork and the dialog, and nothing else.
   *
   * No category bar and no products — which is what the legacy page does, by wrapping its
   * whole React mount and its sidebar cart in
   * `{#if:promoInfo.expired:neq:Y#}{#if:promoInfo.active:neq:N#}` and leaving only the
   * banner block outside it. The reason is commercial rather than tidy: a promotion that
   * is over must stop quoting its prices. `?xEO=Y` brings the whole sale back, because
   * the server resolves the override before any of this.
   *
   * The header and the cart drawer are the layout's and stay, as legacy's header and its
   * cart opener do. They carry nothing of this sale: with no document fetched, the
   * category menu is empty and the search box has nothing to suggest.
   */
  if (status === 'over') {
    return (
      <>
        <SaleBanner banner={banner} />
        <SaleEndedDialog notYetActive={notYetActive} expiryDate={expiryDate} />
      </>
    );
  }

  if (status === 'loading') {
    return <SaleSkeleton />;
  }

  if (status === 'error' || !data) {
    return (
      <Container className={cn('py-16 text-center', SALE_CONTAINER)}>
        <p className="text-slate-700 m-0">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-4xl text-slate-400" />
          <span className="block mt-3">
            There was an error loading the sale. Try again, and if it keeps happening please{' '}
            <a href="/contact">contact us</a>.
          </span>
        </p>
        <Button className="mt-4" onClick={retry}>
          Try again
        </Button>
        {error && <p className="sr-only">{error}</p>}
      </Container>
    );
  }

  return (
    <>
      <SaleBanner banner={banner} />
      <SaleCountdown seconds={expirySeconds} />

      <CategoryNav
        categories={data.categories}
        selectedCategoryId={selection.categoryId}
        onSelect={(categoryId) => select(categoryId)}
      />

      <Container className={cn('pt-5 pb-10', SALE_CONTAINER)}>
        {data.categories.map((category) => (
          <SaleErrorBoundary
            key={category.id}
            fallback={() => (
              <Alert color="danger" className="my-4">
                We could not show the {category.name} section. Everything else on this sale still works.
              </Alert>
            )}
          >
            <CategorySection
              category={category}
              products={data.productsByCategory[category.id] || []}
              restrictions={restrictions}
              highestTerm={highestTerm}
              showCoverPrice={showCoverPrice}
              pinnedProductId={selection.categoryId === category.id ? selection.productId : null}
              detail={detail && detail.categoryId === category.id ? detail : null}
              onDetailClose={() => setDetail(null)}
              onAdd={onAdd}
            />
            {/*
              "Did you know you can gift magazines to your friends?", after the first
              section — where the legacy page put it, with `{index++ === 0 && <Gifting />}`
              inside the category loop.

              Anchored to the first section that actually draws rather than to the first in
              the list, because a section whose every product is restricted for this
              visitor renders nothing at all, and the block would then open the sale
              instead of interrupting it.

              Only on a sale of more than one section, counted as the navbar counts them:
              on a sale of one there is nothing to interrupt, and it closed the page.

              Off unless the brand asks for it — see `showGiftingPromo`.
            */}
            {showGiftingPromo && data.categories.length > 1 && category.id === firstDrawnCategoryId && (
              <GiftingPromo />
            )}
          </SaleErrorBoundary>
        ))}

        {sellsMagazines && (
          <p className="text-xs text-slate-400 text-center mt-8 px-3">
            Magazine covers are used for illustrative purposes only and you may not receive a copy of the
            particular issue depicted. Your subscription will include the most recent issue once your
            subscription begins. Magazine names, logos, and images may be registered trademarks of individual
            magazines, publishers, or parent companies. All subscriptions will automatically renew where
            applicable by law.
          </p>
        )}
      </Container>
    </>
  );
}

MegaSale.layoutName = 'Layouts.MegaSale';
